import React from 'react';
import {FormattedMessage} from 'react-intl';
import {graphql} from 'react-relay';
import Util from '../forecast-app/shared/util/util';
import {createToast} from '../forecast-app/shared/components/toasts/toast';
import * as tracking from '../tracking';
import {MODAL_TYPE, showModal} from '../forecast-app/shared/components/modals/generic_modal_conductor';
import {trackAndCommitMutation} from '../tracking/amplitude/TrackMutation';
import {adjustTaskSetsAfterDelete} from '../forecast-app/project-tab/projects/scoping-page/task-set/taskSetDeleteRenumber';

const mutation = graphql`
	mutation deleteTaskMutation($input: DeleteTaskInput!) {
		deleteTask(input: $input) {
			deletedTasksIds
			deletedTimeRegsIds
			project {
				id
				completion
				remaining
				forecast
				progress
				progressDetails {
					progress
				}
				phases(first: 10000) {
					edges {
						node {
							id
							progress
							progressDetails {
								progress
							}
						}
					}
				}
			}
			parentTask {
				rollupEstimate
				rollupTimeEntries
				rollupRemaining
			}
			errors
		}
	}
`;

function getConfigs(input) {
	const config = [];
	config.push({
		type: 'RANGE_DELETE',
		parentID: 'Vmlld2VyOnFsZGZqYmUyNDM0UlpSRmVlcmc=',
		connectionKeys: [
			{
				key: 'Viewer_tasks',
			},
		],
		pathToConnection: ['viewer', 'tasks'],
		deletedIDFieldName: 'deletedTasksIds',
	});
	config.push({
		type: 'RANGE_DELETE',
		parentID: 'Vmlld2VyOnFsZGZqYmUyNDM0UlpSRmVlcmc=',
		connectionKeys: [
			{
				key: 'Viewer_favouredTasks',
			},
		],
		pathToConnection: ['viewer', 'tasks'],
		deletedIDFieldName: 'deletedTasksIds',
	});
	config.push({
		type: 'RANGE_DELETE',
		parentID: 'Vmlld2VyOnFsZGZqYmUyNDM0UlpSRmVlcmc=',
		connectionKeys: [
			{
				key: 'Viewer_timeRegistrations',
			},
		],
		pathToConnection: ['viewer', 'timeRegistrations'],
		deletedIDFieldName: 'deletedTimeRegsIds',
	});
	if (input.projectIds && input.projectIds.length > 0) {
		input.projectIds.forEach(pid => {
			config.push({
				type: 'RANGE_DELETE',
				parentID: pid,
				connectionKeys: [
					{
						key: 'Project_tasks',
					},
					// This is to handle the new Sprint page V4.
					// It uses two different connections for the sprint task and the backlog.
					{
						key: 'Project_tasks',
						filters: {backlog: true},
					},
				],
				pathToConnection: ['project', 'tasks'],
				deletedIDFieldName: 'deletedTasksIds',
			});
		});
	} else {
		config.push({
			type: 'RANGE_DELETE',
			parentID: input.projectId,
			connectionKeys: [
				{
					key: 'Project_tasks',
				},
				// This is to handle the new Sprint page V4.
				// It uses two different connections for the sprint task and the backlog.
				{
					key: 'Project_tasks',
					filters: {backlog: true},
				},
			],
			pathToConnection: ['project', 'tasks'],
			deletedIDFieldName: 'deletedTasksIds',
		});
		config.push({
			type: 'RANGE_DELETE',
			parentID: input.projectId,
			connectionKeys: [
				{
					key: 'Project_timeRegistrations',
				},
			],
			pathToConnection: ['project', 'timeRegistrations'],
			deletedIDFieldName: 'deletedTimeRegsIds',
		});
	}
	// Needed for My List and Timesheets
	if (input.viewer && input.viewer.company && input.viewer.company.id) {
		config.push({
			type: 'RANGE_DELETE',
			parentID: input.viewer.company.id,
			connectionKeys: [
				{
					key: 'Company_allTasks',
				},
				{
					key: 'Company_taskTypeQueryTasks',
				},
				{
					key: 'Company_modalTasks',
				},
				{
					key: 'Company_overdueTasks',
				},
			],
			pathToConnection: ['company', 'allTasks'],
			deletedIDFieldName: 'deletedTasksIds',
		});
	}
	if (input.filters) {
		let filterConfig = {
			type: 'RANGE_DELETE',
			parentID: input.viewer.company.id,
			connectionKeys: [],
			pathToConnection: ['company', 'allTasks'],
			deletedIDFieldName: 'deletedTasksIds',
		};
		input.filters.forEach(filter => {
			filterConfig.connectionKeys.push({
				key: 'Company_allTasks',
				filters: {filterColumnId: filter.toString()},
			});
		});
		config.push(filterConfig);
	}
	if (input.parentTaskId) {
		config.push({
			type: 'RANGE_DELETE',
			parentID: input.parentTaskId,
			connectionKeys: [
				{
					key: 'ParentTask_childrenTasks',
				},
			],
			pathToConnection: ['parentTask', 'childrenTasks'],
			deletedIDFieldName: 'deletedTasksIds',
		});
	}
	return config;
}

function getOptimisticResponse(input) {
	return {
		deleteTask: {
			deletedTasksIds: input.ids,
		},
	};
}

/**
 * How many of the tasks asked for the server kept. An integration (a Unit4 work order) keeps a task
 * without an error, and the response then leaves its id out of deletedTasksIds.
 */
const countKeptTasks = (input, result) => {
	const deletedIds = result.deleteTask?.deletedTasksIds;
	if (!Array.isArray(deletedIds)) {
		return 0;
	}
	return (input.ids || []).filter(id => !deletedIds.includes(id)).length;
};

const onSuccessWrapper = (result, onSuccess, input) => {
	if (result.deleteTask && result.deleteTask.errors && result.deleteTask.errors.length) {
		const errors = result.deleteTask.errors;
		if (Util.checkForSageErrorAndShowModal(errors)) {
			return;
		} else if (errors[0] === 'TIME_REGISTRATION_IN_LOCKED_PERIOD') {
			showModal({
				type: MODAL_TYPE.WARNING_DATE_LOCKED,
				nonTimeRegWarning: true,
			});
		} else {
			showModal({
				type: MODAL_TYPE.MUTATION_ERROR,
			});
		}
	} else {
		// The kept task's row comes back as Relay drops the optimistic delete, so say why. A
		// FormattedMessage rather than a string: this runs outside any component, and the toaster
		// renders it inside the app's IntlProvider.
		const kept = countKeptTasks(input, result);
		if (kept > 0) {
			createToast({duration: 5000, message: <FormattedMessage id="common.task_delete_kept" values={{count: kept}} />});
		}
		// Nothing was deleted, so the caller's success steps (its "deleted" toast, closing the
		// modal, deselecting rows) would describe something that did not happen.
		if (kept > 0 && kept === input.ids.length) {
			return;
		}
		onSuccess(result);
	}
};

function commit(environment, input, onSuccess, onError) {
	Util.localStorageDeleteFincancialMap();
	tracking.trackEvent('Card Deleted');
	return trackAndCommitMutation(environment, {
		mutation,
		variables: {
			input: {
				csrfToken: Util.getCsrfValue(),
				socketClientId: Util.getClientId(),
				ids: input.ids,
				projectId: input.projectId,
				parentTaskId: input.parentTaskId,
			},
		},
		configs: getConfigs(input),
		optimisticResponse: getOptimisticResponse(input),
		// The ids come from the response, never from the input. A delete the server refuses still
		// arrives as a normal payload, with errors set and no deletedTasksIds, and Relay runs this
		// updater for it all the same; reading the response keeps the chips unchanged then. A task
		// an integration keeps is left out of deletedTasksIds too, so its chip stays as well.
		updater: store =>
			adjustTaskSetsAfterDelete(store, {
				deletedIds: store.getRootField('deleteTask')?.getValue('deletedTasksIds'),
				projectId: input.projectId,
				taskSetInstances: input.taskSetInstances,
			}),
		onCompleted: result => onSuccessWrapper(result, onSuccess, input),
		onError: onError,
	});
}

export default {commit};
